import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer as MapContainerRaw,
  TileLayer as TileLayerRaw,
  Marker as MarkerRaw,
  Popup as PopupRaw,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useChargeStore from '../stores/useChargeStore';
import { useViewportFill } from '../components/ui/viewport';
import Checkbox from '../components/ui/Checkbox';
import { Appear } from '../components/ui/Appear';
import { fmtLastSeen } from './chargeUi';
import type { ChargerView } from './types';

// react-leaflet 3.x types don't expose every leaflet prop; the codebase casts the
// components to permissive `any` (see AirspaceMap.tsx). Runtime is unaffected.
const MapContainer = MapContainerRaw as any;
const TileLayer = TileLayerRaw as any;
const Marker = MarkerRaw as any;
const Popup = PopupRaw as any;

const POLL_MS = 30000;
const ONLINE_COLOR = '#ffcc33';   // primary amber — this location has a live charger
const OFFLINE_COLOR = '#5f5f5f';  // gray — last-known position only

interface Props {
  onOpenCharger: (c: ChargerView) => void;
}

interface Group {
  key: string;
  lat: number;
  lng: number;
  label: string | null;
  chargers: ChargerView[];
  anyOnline: boolean;
}

// One divIcon for a location pin. Multi-charger locations show a count; single
// pins are a plain dot. A live location gets a pulsing ring.
function locationIcon(count: number, color: string, ping: boolean) {
  const size = count > 1 ? Math.min(38, 18 + Math.sqrt(count) * 4) : 14;
  const ring = ping
    ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${size}px;height:${size}px;border-radius:50%;background:${color};opacity:.45;animation:cc-ping 1.6s ease-out infinite;"></div>`
    : '';
  const label = count > 1
    ? `<span style="position:relative;color:#171717;font-weight:700;font-size:${Math.max(10, size / 2.6)}px;z-index:1;">${count}</span>`
    : '';
  return L.divIcon({
    html: `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">
      ${ring}
      <div style="position:relative;width:${size}px;height:${size}px;background:${color};border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.5);z-index:1;">${label}</div>
    </div>`,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

// Fit the map to all pins once, on first data arrival only (later polls don't yank
// the view back while the user is panning).
function FitBounds({ positions, hasInit }: {
  positions: [number, number][]; hasInit: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  useEffect(() => {
    if (hasInit.current || positions.length === 0) return;
    if (positions.length === 1) map.setView(positions[0], 9);
    else map.fitBounds(L.latLngBounds(positions), { padding: [48, 48] });
    hasInit.current = true;
  }, [map, positions, hasInit]);
  return null;
}

export default function ChargeMap({ onOpenCharger }: Props) {
  const searchChargers = useChargeStore((s) => s.searchChargers);
  const [chargers, setChargers] = useState<ChargerView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOffline, setShowOffline] = useState(true);
  const hasInit = useRef(false);
  const mounted = useRef(true);
  const frame = useRef<HTMLDivElement>(null);
  useViewportFill(frame, { mode: 'height', min: 420 });

  // The map wants the WHOLE fleet (not the curated Control set), so it fetches the
  // documented "browse everything" path — /charge/fleet?search= — on its own poll.
  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        const all = await searchChargers('');
        if (mounted.current) { setChargers(all); setLoading(false); }
      } catch {
        if (mounted.current) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { mounted.current = false; clearInterval(t); };
  }, [searchChargers]);

  // The show-offline toggle governs BOTH the plotted pins and the off-map tally.
  const visible = useMemo(
    () => (showOffline ? chargers : chargers.filter((c) => c.online)),
    [chargers, showOffline]
  );

  // Effective location: live geo when online, else the durable last-known location —
  // so an offline charger still plots at its last spot instead of vanishing.
  const coordsOf = (c: ChargerView) => ({
    lat: c.geo_lat ?? c.last_seen_lat,
    lng: c.geo_lng ?? c.last_seen_lng,
    label: c.geo_label ?? c.last_seen_geo,
  });

  // Group by city centroid — collector geo is city-level, so many chargers report
  // identical coords. One pin per location; the popup enumerates what's there.
  const groups = useMemo<Group[]>(() => {
    const m = new Map<string, Group>();
    for (const c of visible) {
      const { lat, lng, label } = coordsOf(c);
      if (lat == null || lng == null) continue;
      const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
      let g = m.get(key);
      if (!g) {
        g = { key, lat, lng, label, chargers: [], anyOnline: false };
        m.set(key, g);
      }
      g.chargers.push(c);
      if (c.online) g.anyOnline = true;
    }
    return [...m.values()];
  }, [visible]);

  const positions = useMemo<[number, number][]>(() => groups.map((g) => [g.lat, g.lng]), [groups]);
  const onMapCount = useMemo(() => groups.reduce((n, g) => n + g.chargers.length, 0), [groups]);
  const noGeoCount = useMemo(
    () => visible.filter((c) => { const { lat, lng } = coordsOf(c); return lat == null || lng == null; }).length,
    [visible]
  );

  const defaultCenter: [number, number] = [39.8283, -98.5795];

  return (
    <div ref={frame} className="relative rounded-md overflow-hidden border border-gray-700">
      <style>{`
        @keyframes cc-ping { 0% { transform: translate(-50%,-50%) scale(1); opacity:.7 } 100% { transform: translate(-50%,-50%) scale(3.6); opacity:0 } }
        .cc-map .leaflet-popup-content-wrapper { background:#171717; color:var(--on-surface); border-radius:4px; }
        .cc-map .leaflet-popup-tip { background:#171717; }
        .cc-map .leaflet-popup-close-button { color:var(--on-surface-variant) !important; }
        .cc-map .leaflet-popup-close-button:hover { color:#ffcc33 !important; }
      `}</style>

      {/* show-offline toggle */}
      <div className="absolute top-3 right-3 z-[1000] bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 text-sm">
        <Checkbox label="Show offline" checked={showOffline}
          onChange={(e) => setShowOffline(e.target.checked)} />
      </div>

      <MapContainer center={defaultCenter} zoom={4} className="cc-map"
        style={{ height: '100%', width: '100%' }}
        zoomControl={false} attributionControl={false} maxZoom={18}>
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxNativeZoom={18} maxZoom={18} />
        <FitBounds positions={positions} hasInit={hasInit} />
        {groups.map((g) => {
          const color = g.anyOnline ? ONLINE_COLOR : OFFLINE_COLOR;
          return (
            <Marker key={g.key} position={[g.lat, g.lng]}
              icon={locationIcon(g.chargers.length, color, g.anyOnline)}>
              <Popup>
                <div className="min-w-[180px]">
                  <p className="text-primary font-bold border-b border-primary/60 pb-1 mb-1.5">
                    {g.label || 'Unknown location'}
                  </p>
                  <ul className="space-y-1">
                    {g.chargers.map((c) => (
                      <li key={c.vpn_ip} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${c.online ? 'bg-green-400' : 'bg-outline'}`} />
                          <button type="button" onClick={() => onOpenCharger(c)}
                            className="truncate text-on-surface hover:text-primary underline decoration-dotted">
                            {c.display_code || c.vpn_ip}
                          </button>
                        </span>
                        <span className="text-[11px] text-on-surface-variant flex-shrink-0">
                          {c.online ? 'now' : fmtLastSeen(c.last_seen) || '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      <Appear show={loading} as="div" className="absolute inset-0 flex items-center justify-center z-[500] pointer-events-none">
        <div className="bg-black/70 px-4 py-2 rounded text-on-surface text-sm">Loading chargers…</div>
      </Appear>
      <Appear show={!loading} as="div" className="absolute bottom-3 left-3 z-[1000] bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-on-surface">
        {onMapCount} on map
        {noGeoCount > 0 && <span className="text-on-surface-variant"> · {noGeoCount} without location</span>}
      </Appear>
      <div className="absolute bottom-3 right-3 z-[1000] text-[11px] text-on-surface-variant bg-black/60 rounded px-2 py-1">
        Approx. city-level
      </div>
    </div>
  );
}
